import { downscaleToLimit, reencodeToJpeg } from './downscale'
import { renameToMatchBlob } from './image-file-name'

const HeicMimeTypes = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']

const HeicExtensions = ['.heic', '.heif']

export class HeicConvertError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message)
    this.name = 'HeicConvertError'
  }
}

/** iOS のファイル選択では `type` が空文字で来ることがあるため、その場合は拡張子で判定する */
export const isHeic = (file: Blob | File): boolean => {
  const type = file.type?.toLowerCase() ?? ''
  if (HeicMimeTypes.includes(type)) {
    return true
  }
  if (type) {
    return false
  }
  const name = 'name' in file ? file.name.toLowerCase() : ''
  return HeicExtensions.some(extension => name.endsWith(extension))
}

export const convertHeicToJpeg = async (file: Blob | File): Promise<Blob> => {
  if (!isHeic(file)) {
    return file
  }

  try {
    const { default: heic2any } = await import('heic2any')
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 })
    const blob = Array.isArray(converted) ? converted[0] : converted
    if (!blob) {
      throw new HeicConvertError('HEIC を変換できませんでした')
    }
    return blob
  } catch (error) {
    throw error instanceof HeicConvertError ? error : new HeicConvertError('HEIC を変換できませんでした', error)
  }
}

// Rekognition がそのまま読める画像形式。バックエンドはサイズも形式も弾かずそのまま渡すため、
// これ以外を上げると判定が失敗し、ゲストには「通信失敗・再試行」としか見えない
const RekognitionMimeTypes = ['image/jpeg', 'image/png']

const RekognitionExtensions = ['.jpg', '.jpeg', '.png']

// `<input accept="image/*">` は選択画面の絞り込みにしかならず、実際に選べる形式は
// ブラウザと OS 次第のため、ここで判定して弾く
export const needsJpegReencode = (file: Blob | File): boolean => {
  if (isHeic(file)) {
    return false
  }
  const type = file.type?.toLowerCase() ?? ''
  if (type) {
    return !RekognitionMimeTypes.includes(type)
  }
  const name = 'name' in file ? file.name.toLowerCase() : ''
  return !RekognitionExtensions.some(extension => name.endsWith(extension))
}

export type PreparedImage = { blob: Blob; fileName: string }

export const prepareImageForUpload = async (file: Blob | File, fileName: string): Promise<PreparedImage> => {
  const heicConverted = await convertHeicToJpeg(file)
  const jpegOrPng = needsJpegReencode(heicConverted) ? await reencodeToJpeg(heicConverted) : heicConverted
  // HEIC → JPEG 変換後も 5MB を超えることがあるため、変換の後に縮小を通す
  const blob = await downscaleToLimit(jpegOrPng)
  // `convertHeicToJpeg` と `downscaleToLimit` は変換しないとき入力の Blob をそのまま返し、
  // `reencodeToJpeg` は必要なときだけ呼ぶので、同一性で「変換したか」が分かる
  if (blob === file) {
    return { blob, fileName }
  }
  return { blob, fileName: renameToMatchBlob(fileName, blob) }
}
